import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, CreditCard, ShieldCheck, Info, Truck, Store, MapPin, Clock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, formatPrice } from "@/lib/api";
import { useSquareCard } from "@/lib/useSquareCard";
import { useStoreInfo } from "@/context/StoreInfoContext";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const store = useStoreInfo();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [env, setEnv] = useState("sandbox");
  const [fulfillment, setFulfillment] = useState("ship"); // "ship" | "pickup"
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
  });

  const { ready: cardReady, error: cardError, tokenize } = useSquareCard("square-card-container");

  useEffect(() => {
    api.get("/square/config").then((r) => setEnv(r.data.environment || "sandbox")).catch(() => {});
  }, []);

  if (items.length === 0) return <Navigate to="/cart" replace />;

  const isProd = env === "production";
  const isPickup = fulfillment === "pickup";
  const shipping = isPickup || subtotal >= 50 ? 0 : 6.99;
  const tax = +(subtotal * 0.06).toFixed(2);
  const total = +(subtotal + shipping + tax).toFixed(2);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const requiredBase = ["name", "email"];
    const requiredShip = isPickup ? [] : ["address", "city", "state", "postal_code"];
    for (const k of [...requiredBase, ...requiredShip]) {
      if (!form[k]) {
        toast.error("Please fill in all required fields");
        return;
      }
    }
    setSubmitting(true);
    let sourceId = null;
    try {
      if (cardReady) {
        try {
          sourceId = await tokenize();
        } catch (tokErr) {
          toast.error("Card error", { description: tokErr.message });
          setSubmitting(false);
          return;
        }
      }
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        customer: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          address: isPickup ? "" : form.address,
          city: isPickup ? "" : form.city,
          state: isPickup ? "" : form.state,
          postal_code: isPickup ? "" : form.postal_code,
        },
        square_source_id: sourceId,
        fulfillment_type: fulfillment,
      });
      clear();
      toast.success(
        data.payment_provider === "square" ? "Payment complete!" : "Order placed!",
        { description: `Order ${data.order_id}` }
      );
      navigate("/order-success", { state: { order: data } });
    } catch (err) {
      toast.error("Payment failed", { description: err?.response?.data?.detail || "Try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="checkout-page">
      <h1 className="font-display text-3xl font-bold text-brand-navy sm:text-4xl">
        Secure Checkout
      </h1>
      <p className="mt-2 flex items-center gap-2 text-sm text-slate-600">
        <Lock size={14} className="text-brand-teal" /> Powered by Square
        {!isProd && <span className="chip bg-amber-100 text-amber-800">Sandbox</span>}
        {isProd && <span className="chip bg-emerald-100 text-emerald-800">Live · Real payments</span>}
      </p>

      <form onSubmit={submit} className="mt-8 grid gap-8 lg:grid-cols-[1fr,400px]">
        <div className="space-y-8">
          {/* Delivery method selector */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="fulfillment-section">
            <h2 className="font-display text-lg font-bold text-brand-navy">How would you like your order?</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setFulfillment("ship")}
                aria-pressed={!isPickup}
                data-testid="fulfillment-ship-option"
                className={`flex items-start gap-3 rounded-xl border-2 p-4 text-left transition ${
                  !isPickup
                    ? "border-brand-teal bg-brand-teal/5"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className={`mt-0.5 rounded-lg p-2 ${!isPickup ? "bg-brand-teal text-white" : "bg-slate-100 text-slate-500"}`}>
                  <Truck size={18} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-display font-bold text-brand-navy">Ship to me</p>
                    <span className="text-xs font-bold text-slate-500">
                      {subtotal >= 50 ? "FREE" : "$6.99"}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    USPS Ground · 3–7 business days · Free on orders $50+
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setFulfillment("pickup")}
                aria-pressed={isPickup}
                data-testid="fulfillment-pickup-option"
                className={`flex items-start gap-3 rounded-xl border-2 p-4 text-left transition ${
                  isPickup
                    ? "border-brand-teal bg-brand-teal/5"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className={`mt-0.5 rounded-lg p-2 ${isPickup ? "bg-brand-teal text-white" : "bg-slate-100 text-slate-500"}`}>
                  <Store size={18} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-display font-bold text-brand-navy">In-store pickup</p>
                    <span className="text-xs font-bold text-brand-teal">FREE</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    Skip shipping · Usually ready in 2 hours
                  </p>
                </div>
              </button>
            </div>

            {isPickup && (
              <div className="mt-5 rounded-xl bg-brand-teal/5 p-4" data-testid="pickup-details">
                <p className="text-xs font-bold uppercase tracking-widest text-brand-teal">Pick up at</p>
                <p className="mt-1 font-semibold text-brand-navy">{store.name}</p>
                {store.address_full && (
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-600">
                    <MapPin size={13} className="text-brand-teal" /> {store.address_full}
                  </p>
                )}
                {store.hours && (
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-600">
                    <Clock size={13} className="text-brand-teal" /> {store.hours}
                  </p>
                )}
                <p className="mt-3 text-xs text-slate-500">
                  We&apos;ll email you the moment your order is ready. Bring photo ID and your order number.
                </p>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="font-display text-lg font-bold text-brand-navy">
              {isPickup ? "Pickup contact" : "Customer information"}
            </h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Field label="Full name*">
                <input required value={form.name} onChange={change("name")} data-testid="checkout-name" className={inputCls} />
              </Field>
              <Field label="Email*">
                <input required type="email" value={form.email} onChange={change("email")} data-testid="checkout-email" className={inputCls} />
              </Field>
              <Field label={isPickup ? "Phone (recommended for pickup notify)" : "Phone"} full={isPickup}>
                <input value={form.phone} onChange={change("phone")} data-testid="checkout-phone" className={inputCls} />
              </Field>
            </div>
          </section>

          {!isPickup && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="shipping-address-section">
              <h2 className="font-display text-lg font-bold text-brand-navy">Shipping address</h2>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <Field label="Street address*" full>
                  <input required value={form.address} onChange={change("address")} data-testid="checkout-address" className={inputCls} />
                </Field>
                <Field label="City*">
                  <input required value={form.city} onChange={change("city")} data-testid="checkout-city" className={inputCls} />
                </Field>
                <Field label="State*">
                  <input required value={form.state} onChange={change("state")} data-testid="checkout-state" className={inputCls} />
                </Field>
                <Field label="ZIP / Postal*">
                  <input required value={form.postal_code} onChange={change("postal_code")} data-testid="checkout-postal" className={inputCls} />
                </Field>
              </div>
            </section>
          )}

          <section className="rounded-2xl border-2 border-brand-teal/30 bg-white p-6">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold text-brand-navy">
              <CreditCard size={18} className="text-brand-teal" /> Payment · Square
            </h2>
            <p className="mt-1 flex items-start gap-2 text-xs text-slate-500">
              <Info size={12} className="mt-0.5 shrink-0 text-brand-teal" />
              {isProd
                ? "Cards are tokenized in your browser via Square — we never see the number. Your card will be charged for the total shown."
                : "Sandbox test card: 4111 1111 1111 1111, any future exp, any CVV, ZIP 94103. Cards are tokenized in your browser — we never see the number."}
            </p>

            <div
              id="square-card-container"
              className="mt-4 min-h-[56px] rounded-lg border border-slate-200 bg-white p-3"
              data-testid="square-card-container"
            />

            {cardError && (
              <p className="mt-2 text-xs text-red-600" data-testid="square-card-error">
                Card unavailable: {cardError}
              </p>
            )}
            {!cardReady && !cardError && (
              <p className="mt-2 text-xs text-slate-500">Loading secure card entry…</p>
            )}
          </section>
        </div>

        <aside className="sticky top-28 h-fit rounded-2xl border border-slate-200 bg-white p-6" data-testid="checkout-summary">
          <h2 className="font-display text-lg font-bold text-brand-navy">Order summary</h2>
          <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-brand-teal" data-testid="summary-fulfillment">
            {isPickup ? "In-store pickup" : "Ship to me"}
          </p>
          <ul className="mt-4 max-h-80 space-y-3 overflow-auto text-sm">
            {items.map((i) => (
              <li key={i.id} className="flex justify-between gap-3 text-slate-700">
                <span className="line-clamp-2">
                  {i.quantity} × {i.name}
                </span>
                <span className="font-semibold text-brand-navy">{formatPrice(i.price * i.quantity)}</span>
              </li>
            ))}
          </ul>
          <dl className="mt-6 space-y-2 border-t border-slate-200 pt-4 text-sm">
            <Row label="Subtotal" value={formatPrice(subtotal)} />
            <Row label={isPickup ? "Pickup" : "Shipping"} value={shipping === 0 ? "Free" : formatPrice(shipping)} />
            <Row label="Tax (6%)" value={formatPrice(tax)} />
          </dl>
          <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4 font-display text-xl font-extrabold text-brand-navy">
            <span>Total</span>
            <span data-testid="checkout-total">{formatPrice(total)}</span>
          </div>
          <button
            type="submit"
            disabled={submitting || (!cardReady && !cardError)}
            className="btn-accent mt-6 flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-bold uppercase tracking-wide disabled:bg-slate-300"
            data-testid="checkout-submit-button"
          >
            <ShieldCheck size={16} /> {submitting ? "Processing…" : `Pay ${formatPrice(total)}`}
          </button>
          <p className="mt-3 text-center text-[11px] text-slate-500">
            {isProd ? "Payments securely processed by Square." : "Sandbox mode — no real money is charged."}
          </p>
        </aside>
      </form>
    </div>
  );
}

const inputCls =
  "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/30";

function Field({ label, children, full }) {
  return (
    <label className={`block text-xs font-semibold text-slate-600 ${full ? "md:col-span-2" : ""}`}>
      <span className="mb-1 block uppercase tracking-wider">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-slate-600">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
